import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';

/* Shared toast system. Replaces MoneyCell's bespoke inline-styled toast.
   Any component inside <ToastHost> can call useToast() and fire a message.

   Usage:
     const toast = useToast();
     toast('Copied money amount');
     toast({ message: 'Save failed', tone: 'error' });
*/
const ToastContext = createContext(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    /* Graceful no-op if host isn't mounted — avoids hard-crashing during
       testing or partial mounts. */
    return () => {};
  }
  return ctx;
}

let idSeq = 0;

export function ToastHost({ children }) {
  const [items, setItems] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    setItems(prev => prev.filter(t => t.id !== id));
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  const show = useCallback((arg) => {
    const payload = typeof arg === 'string' ? { message: arg } : (arg ?? {});
    const id = ++idSeq;
    const tone = payload.tone ?? 'success';
    const duration = payload.duration ?? 1400;
    setItems(prev => [...prev, { id, message: payload.message ?? '', tone }]);
    timers.current[id] = setTimeout(() => dismiss(id), duration);
  }, [dismiss]);

  const toneClass = {
    success: 'border-accent-green text-accent-green',
    error:   'border-accent-red text-accent-red',
    info:    'border-accent-blue text-accent-blue',
    warn:    'border-accent-gold text-accent-gold',
  };

  return (
    <ToastContext.Provider value={show}>
      {children}
      {typeof document !== 'undefined' && createPortal(
        <div className="fixed bottom-6 right-6 flex flex-col gap-2 z-[300] pointer-events-none">
          {items.map(t => (
            <div
              key={t.id}
              className={`bg-[rgba(18,21,28,0.95)] border ${toneClass[t.tone] ?? toneClass.success} rounded-md px-3 py-2 text-[0.78rem] shadow-lg pointer-events-auto font-sans backdrop-blur-sm transition-opacity animate-in fade-in`}
            >
              {t.message}
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}
